/* eslint-disable no-console */
import { Dialog, Transition } from '@headlessui/react';
import React, { Fragment, useState, useContext } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import mixpanel from 'mixpanel-browser';

import { Card } from '../Card';
import { ArrowRight } from '../../Icons';
import { formatAmount } from '../../helpers/shared';
import { SignIn } from '../SignIn';
import { PriceContext } from '../../context/priceContext';

type Props = {
  userId?: string | null;
  email: string;
  isOpen: boolean;
  isTrial: boolean;
  hasPremium: boolean;
  onClose: () => void;
};

const PaywallModal = ({ userId, isOpen, email, isTrial, hasPremium, onClose }: Props) => {
  const [isAuthOpen, setAuthOpen] = useState(false);

  const { price } = useContext(PriceContext);

  const { monthly, annually } = price || {};

  const sendDataToMixpanel = async (event: any) => {
    if (event.target.price.value === annually.price) {
      mixpanel.track('Annual subscription tapped', {
        premium: hasPremium,
        trial: isTrial,
      });
    } else if (event.target.price.value === monthly.price) {
      mixpanel.track('Month subscription tapped', {
        premium: hasPremium,
        trial: isTrial,
      });
    }
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();

    await sendDataToMixpanel(event);

    if (userId) {
      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');
      if (stripe) {
        const data = {
          sku: event.target.price.value,
          userId,
          email,
        };
        const response = await fetch('/.netlify/functions/create-checkout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }).then((res) => res.json());
        const { error } = await stripe.redirectToCheckout({
          sessionId: response.sessionId,
        });
        console.log(error);
      }
    } else {
      setAuthOpen(true);
    }
  };

  const handleAuthClose = () => {
    setAuthOpen(false);
  };

  const handleAuthSuccess = () => {
    setAuthOpen(false);
  };

  // const handleAnnualClick = () => {
  //   mixpanel.track('Annual subscription tapped', {
  //     premium: hasPremium,
  //     trial: isTrial,
  //   });
  // };

  // const handleMontlyClick = () => {
  //   mixpanel.track('Month subscription tapped', {
  //     premium: hasPremium,
  //     trial: isTrial,
  //   });
  // };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as='div' className='fixed z-10 inset-0 overflow-y-auto' onClose={onClose}>
        <div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
          </Transition.Child>

          <span className='hidden sm:inline-block sm:align-middle sm:h-screen' aria-hidden='true'>
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            enterTo='opacity-100 translate-y-0 sm:scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          >
            <div
              className={`
              relative 
            inline-block 
            overflow-hidden 
            transform 
            transition-all s
            align-middle 
            w-[683px]
            md:w-full
            md:max-w-[380px]
            `}
            >
              <Card theme='dark' size='large' onClose={onClose}>
                <div className='relative flex flex-col max-h-[95vh] md:max-h-fit min-h-[600px]'>
                  <div
                    className={`h-[380px] grow-0
                bg-paywall-pattern 
                rounded-t-[22px] flex flex-col justify-end items-start p-8 md:p-6 md:h-[240px]`}
                  >
                    <h1 className='text-5xl leading-5xl font-bold text-white text-left md:text-4xl md:leading-4xl'>
                      Tools for procreate premium
                    </h1>
                  </div>
                  <div className='p-8 text-left md:p-6 grow flex flex-col'>
                    <p className='text-base leading-base text-white max-w-[407px] mb-6 md:text-sm md:leading-sm grow-0'>
                      Unlimited access to 1,050+ tools for Procreate with weekly updates.
                    </p>
                    <div className='flex mb-9 md:flex-col md:mb-4 grow justify-between'>
                      <form
                        onSubmit={handleSubmit}
                        action='/.netlify/functions/create-checkout'
                        method='post'
                        className='grow'
                      >
                        {/* @ts-ignore */}
                        <input type='hidden' name='price' value={(annually && annually.price) || ''} />
                        <button
                          type='submit'
                          className={`bg-main-blue 
                          p-8 pt-6 pr-6 
                          md:p-4
                          w-full
                          text-white rounded-[16px] 
                          cursor-pointer text-left md:w-full h-full flex flex-col justify-between`}
                        >
                          <div>
                            <p
                              className={`text-4xl leading-4xl 
                          font-bold md:text-2xl md:leading-2xl sm:text-2xl sm:leading-2xl`}
                            >
                              {annually ? formatAmount(annually.amount) : '$39.99'} annually
                            </p>
                            <p className='text-base leading-base md:text-sm md:leading-sm'>
                              {annually ? formatAmount(annually.amount / 12) : ''} / mo
                            </p>
                          </div>

                          <div className='flex justify-between items-center w-full mt-16 md:mt-4'>
                            <p className='text-base leading-base md:text-sm md:leading-sm'>Save 70%</p>
                            <div className='cursor-pointer'>
                              <ArrowRight width={32} height={32} />
                            </div>
                          </div>
                        </button>
                      </form>

                      <form
                        onSubmit={handleSubmit}
                        action='/.netlify/functions/create-checkout'
                        method='post'
                        className='grow ml-6 md:ml-0'
                      >
                        <button
                          type='submit'
                          className={`
                      bg-main-blue 
                      p-8 pt-6 pr-6
                      md:p-4
                      w-full
                      text-white rounded-[16px] 
                      md:ml-0 md:mt-4 
                      cursor-pointer text-left md:w-full h-full flex flex-col justify-between`}
                        >
                          <input type='hidden' name='price' value={(monthly && monthly.price) || ''} />
                          <div>
                            <p
                              className={`text-4xl leading-4xl 
                            font-bold md:text-2xl md:leading-2xl sm:text-2xl sm:leading-2xl`}
                            >
                              {monthly ? formatAmount(monthly.amount) : '$7.99'} 1st month
                            </p>
                            <p className='text-base leading-base md:text-sm md:leading-sm'>Then $9,99</p>
                          </div>
                          <div className='flex justify-end w-full mt-16 md:mt-4'>
                            <ArrowRight width={32} height={32} />
                          </div>
                        </button>
                      </form>
                    </div>
                    <p className='text-white text-center grow-0'>
                      <a
                        className='underline hover:no-underline'
                        // eslint-disable-next-line max-len
                        href='https://endurable-weaver-d97.notion.site/Terms-Conditions-Tools-fbdcabcb12d942948055c022c3cf226b'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        Terms of Use
                      </a>{' '}
                      &amp;{' '}
                      <a
                        className='underline hover:no-underline'
                        // eslint-disable-next-line max-len
                        href='https://endurable-weaver-d97.notion.site/Privacy-Policy-Tools-Products-87ad109325ae476a903172a44f41b393'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        Privacy Policy
                      </a>
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </Transition.Child>
        </div>
        <SignIn isOpen={isAuthOpen} onClose={handleAuthClose} onSuccess={handleAuthSuccess} />
      </Dialog>
    </Transition.Root>
  );
};

export { PaywallModal };
